import React from 'react';
import '@/utils/legacyColor.css';

interface HighlightedSection {
  text: string;
  start_index: number;
  end_index: number;
}

interface HighlightedTextProps {
  text: string;
  highlightedSections?: HighlightedSection[];
  className?: string;
  highlightStyle?: React.CSSProperties;
}

const HighlightedText: React.FC<HighlightedTextProps> = ({
  text,
  highlightedSections = [],
  className = '',
  highlightStyle = {},
}) => {
  // If no text, return empty
  if (!text) {
    return <span className={className}></span>;
  }

  // If no highlighted sections, return plain text
  if (!highlightedSections || !highlightedSections.length) {
    return <span className={className}>{text}</span>;
  }

  // Sort sections by start index
  const sortedSections = [...highlightedSections].sort(
    (a, b) => a.start_index - b.start_index
  );

  const defaultHighlightStyle = {
    color: 'var(--zl-highlight-text, #1565c0)',
    fontWeight: '600',
    ...highlightStyle,
  };

  const renderHighlightedText = () => {
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;

    // Render highlighted text sections

    sortedSections.forEach((section, index) => {
      // Add text before the highlighted section
      if (section.start_index > lastIndex) {
        const beforeText = text.slice(lastIndex, section.start_index);
        elements.push(<span key={`text-${index}`}>{beforeText}</span>);
      }

      // Get the actual text from the original text using indices
      const highlightedText = text.slice(
        section.start_index,
        section.end_index
      );

      // Add the highlighted section - use the text from the original string, not from section.text
      elements.push(
        <span key={`highlight-${index}`} style={defaultHighlightStyle}>
          {highlightedText}
        </span>
      );

      lastIndex = section.end_index;
    });

    // Add remaining text after the last highlighted section
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      elements.push(<span key='text-end'>{remainingText}</span>);
    }

    return elements;
  };

  try {
    return (
      <div className={className} style={{ lineHeight: '1.6' }}>
        {renderHighlightedText()}
      </div>
    );
  } catch {
    // Fallback to plain text if highlighting fails
    return <span className={className}>{text}</span>;
  }
};

export default HighlightedText;
